import React, { useState } from 'react'
import { DatePicker, Popover, Select } from 'antd'
import moment from 'moment'
import 'moment/locale/sk'

export type TemplatePattern = 'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY'

export interface TemplateFrequencyValue {
	pattern: TemplatePattern
	weekDays: string[]
	monthDay: number | null
	completionWindowHours: number | null
	startDate: string | null
}

interface Props {
	value: TemplateFrequencyValue
	onChange: (v: TemplateFrequencyValue) => void
}

const PATTERN_OPTIONS: { value: TemplatePattern; label: string }[] = [
	{ value: 'DAILY', label: 'Denne' },
	{ value: 'WEEKDAYS', label: 'Pondelok až piatok' },
	{ value: 'WEEKLY', label: 'Týždenne' },
	{ value: 'MONTHLY', label: 'Mesačne' }
]

const WEEK_DAYS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']
const DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const DAY_SK: Record<string, string> = {
	MON: 'Po', TUE: 'Ut', WED: 'St', THU: 'Št', FRI: 'Pi', SAT: 'So', SUN: 'Ne'
}

const inputStyle: React.CSSProperties = {
	border: '1px solid #d1d5db',
	borderRadius: 6,
	padding: '3px 8px',
	fontSize: 13,
	width: 72,
	outline: 'none',
	color: '#374151',
	fontFamily: 'inherit'
}

const formatPillLabel = (v: TemplateFrequencyValue): string => {
	let patternLabel = ''
	if (v.pattern === 'DAILY') patternLabel = 'Denne'
	else if (v.pattern === 'WEEKDAYS') patternLabel = 'Po–Pi'
	else if (v.pattern === 'WEEKLY') {
		const dayLabels = v.weekDays.map((d) => DAY_SK[d] || d).join(', ')
		patternLabel = dayLabels ? `Týždenne · ${dayLabels}` : 'Týždenne'
	} else if (v.pattern === 'MONTHLY') {
		patternLabel = v.monthDay ? `Mesačne · ${v.monthDay}.` : 'Mesačne'
	}
	const parts = [patternLabel]
	if (v.startDate) parts.push(`od ${moment(v.startDate).format('D. M. YYYY')}`)
	if (v.completionWindowHours) parts.push(`${v.completionWindowHours} h`)
	return parts.join(' · ')
}

const TemplateFrequencyPicker = ({ value, onChange }: Props) => {
	const [open, setOpen] = useState(false)
	const [draft, setDraft] = useState<TemplateFrequencyValue>(value)
	const [weekDayError, setWeekDayError] = useState(false)

	const handleVisibleChange = (visible: boolean) => {
		if (visible) {
			setDraft(value)
			setWeekDayError(false)
		}
		setOpen(visible)
	}

	const handlePatternChange = (pattern: TemplatePattern) => {
		setWeekDayError(false)
		setDraft((prev) => ({ ...prev, pattern }))
	}

	const handleToggleWeekDay = (key: string) => {
		setWeekDayError(false)
		setDraft((prev) => {
			const active = prev.weekDays.includes(key)
			return {
				...prev,
				weekDays: active ? prev.weekDays.filter((d) => d !== key) : [...prev.weekDays, key]
			}
		})
	}

	const handleSave = () => {
		if (draft.pattern === 'WEEKLY' && draft.weekDays.length === 0) {
			setWeekDayError(true)
			return
		}
		onChange(draft)
		setOpen(false)
	}

	const popoverContent = (
		<div style={{ width: 300 }}>
			{/* Header */}
			<div style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: '10px 14px 6px',
				borderBottom: '1px solid #e5e7eb'
			}}>
				<span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Opakovanie</span>
				<button
					type="button"
					onClick={() => setOpen(false)}
					style={{
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: '#9ca3af',
						fontSize: 20,
						lineHeight: 1,
						padding: '0 2px'
					}}
				>
					×
				</button>
			</div>

			{/* Body */}
			<div style={{ padding: '10px 14px' }}>
				{/* Pattern select */}
				<div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Frekvencia</div>
				<Select
					value={draft.pattern}
					onChange={handlePatternChange}
					style={{ width: '100%' }}
					size="small"
				>
					{PATTERN_OPTIONS.map((opt) => (
						<Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
					))}
				</Select>

				{/* WEEKLY: day-of-week picker */}
				{draft.pattern === 'WEEKLY' && (
					<div style={{ marginTop: 8 }}>
						<div style={{
							display: 'flex',
							gap: 4,
							padding: weekDayError ? '6px 8px' : undefined,
							borderRadius: weekDayError ? 6 : undefined,
							border: weekDayError ? '1px solid #ef4444' : undefined,
							background: weekDayError ? '#fef2f2' : undefined
						}}>
							{WEEK_DAYS.map((label, idx) => {
								const key = DAY_KEYS[idx]
								const active = draft.weekDays.includes(key)
								return (
									<button
										key={key}
										type="button"
										onClick={() => handleToggleWeekDay(key)}
										style={{
											width: 32,
											height: 32,
											borderRadius: '50%',
											border: 'none',
											cursor: 'pointer',
											fontSize: 12,
											fontWeight: 600,
											background: active ? '#1d4ed8' : '#f3f4f6',
											color: active ? '#ffffff' : '#374151'
										}}
									>
										{label}
									</button>
								)
							})}
						</div>
						{weekDayError && (
							<div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
								Vyber aspoň jeden deň opakovania.
							</div>
						)}
					</div>
				)}

				{/* MONTHLY: day of month */}
				{draft.pattern === 'MONTHLY' && (
					<div style={{
						display: 'flex', alignItems: 'center', gap: 8, marginTop: 8
					}}>
						<span style={{ fontSize: 13, color: '#374151', minWidth: 60 }}>Deň v mesiaci</span>
						<input
							type="number"
							min={1}
							max={31}
							value={draft.monthDay ?? ''}
							onChange={(e) => {
								const raw = e.target.value
								const parsed = raw === '' ? null : parseInt(raw, 10)
								setDraft((prev) => ({ ...prev, monthDay: parsed !== null && !Number.isNaN(parsed) ? parsed : null }))
							}}
							placeholder="1"
							style={inputStyle}
						/>
						<span style={{ fontSize: 13, color: '#374151' }}>.</span>
					</div>
				)}

				{/* Start date */}
				<div style={{ marginTop: 12 }}>
					<div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Dátum začatia</div>
					<DatePicker
						value={draft.startDate ? moment(draft.startDate) : null}
						onChange={(date) => setDraft((prev) => ({ ...prev, startDate: date ? date.format('YYYY-MM-DD') : null }))}
						format="D. M. YYYY"
						size="small"
						style={{ width: '100%' }}
						placeholder="Vyber dátum"
						allowClear
						getPopupContainer={(trigger) => (trigger.closest('.ant-popover') as HTMLElement) ?? document.body}
					/>
				</div>

				{/* Completion window hours */}
				<div style={{
					display: 'flex', alignItems: 'center', gap: 8, marginTop: 12
				}}>
					<span style={{ fontSize: 13, color: '#374151', minWidth: 100 }}>Čas na splnenie</span>
					<input
						type="number"
						min={0}
						value={draft.completionWindowHours ?? ''}
						onChange={(e) => {
							const raw = e.target.value
							const parsed = raw === '' ? null : parseInt(raw, 10)
							setDraft((prev) => ({ ...prev, completionWindowHours: parsed !== null && !Number.isNaN(parsed) ? parsed : null }))
						}}
						placeholder="0"
						style={inputStyle}
					/>
					<span style={{ fontSize: 13, color: '#374151' }}>hodín</span>
				</div>
			</div>

			{/* Footer */}
			<div style={{
				display: 'flex',
				gap: 8,
				padding: '10px 14px',
				borderTop: '1px solid #e5e7eb',
				justifyContent: 'flex-end'
			}}>
				<button
					type="button"
					onClick={handleSave}
					style={{
						padding: '4px 14px',
						border: 'none',
						borderRadius: 6,
						background: '#2563eb',
						cursor: 'pointer',
						fontSize: 13,
						color: '#fff',
						fontWeight: 600
					}}
				>
					Uložiť
				</button>
			</div>
		</div>
	)

	return (
		<Popover
			visible={open}
			onVisibleChange={handleVisibleChange}
			content={popoverContent}
			trigger="click"
			placement="bottomLeft"
			overlayInnerStyle={{ padding: 0 }}
			getPopupContainer={(trigger) => (trigger.closest('.ant-drawer-body') as HTMLElement) ?? document.body}
		>
			<div style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 6,
				padding: '4px 12px',
				border: '1px solid #d1d5db',
				borderRadius: 20,
				background: '#fff',
				cursor: 'pointer',
				fontSize: 13,
				color: '#374151',
				userSelect: 'none'
			}}>
				{formatPillLabel(value)}
			</div>
		</Popover>
	)
}

export default TemplateFrequencyPicker
